/**
 * Organization subscription write helper (ATL-851 → ATL-855).
 *
 * ## What changed, and why the Firestore write left the client
 *
 * ATL-851 made these call sites resolve a real Firestore `DocumentReference` in both read modes,
 * which stopped the crash. It did NOT close the gap: the write still landed in Firestore only, so
 * a cut-over org (`use_supabase_for_data = true`, reads served by `GET /organizations`) could not
 * change its plan at all.
 *
 * The obvious repair — add a Supabase half here — was rejected, and this file is where that
 * decision lives:
 *
 * 1. **A client-supplied plan is a revenue hole.** `restorePurchases` takes the plan straight off
 *    the device (`customerInfo.entitlements.active[0].product_id`) with nothing verifying it. An
 *    endpoint that accepted it would let any authenticated user grant their own org a paid plan in
 *    the store the Phase-5 cutover makes customer-facing.
 * 2. **A blind write downgrades payers.** `PaywallScreen`'s Android effect writes
 *    `subscriptionPlan: 'free'` unconditionally on mount. Mirroring that as-is reproduces ATL-760
 *    (the HIM.HOUSE ASTANA incident) on the Supabase side — and measurably so: 54 of the 55
 *    organizations currently holding an active paid plan sit at `subscription_expiration_ms = 0`,
 *    so the guard the webhook uses for its bootstrap arm would have admitted every one of those
 *    downgrades.
 *
 * So the write is now **server-authoritative**. The client states its INTENT ("reconcile my
 * subscription") and the backend decides: it derives the entitlement from the provider webhooks it
 * has stored, refuses anything that would downgrade a live paid entitlement, and writes BOTH
 * stores itself (Firestore first and fatally, as the webhooks do). See
 * `apps/atlas-backend/src/modules/organizations/organization-subscription.service.ts`.
 *
 * ## Why there is no Firestore fallback here
 *
 * Deliberate. A local Firestore write that runs when the API call fails is exactly the unguarded
 * second writer this change exists to remove — it would put a plan into the store the app reads
 * that the store of record refused. If the backend is unreachable, nothing is written anywhere,
 * which leaves the two stores consistent rather than divergent.
 */
import { organizationsApi } from '../API/typedApiClient'
import type { SubscriptionSyncResult, OverrideSubscription } from '@atlas/api-client'

/** Resolve the Firestore organization id the backend keys on, from the organization object. */
const resolveOrganizationId = (organization: unknown): string => {
  const org = organization as { firestoreId?: string; id?: string; ref?: { id?: string } } | null
  const id = org?.firestoreId || org?.id || org?.ref?.id
  if (!id) {
    throw new Error(
      'Cannot resolve an organization id for the subscription write — pass the organization ' +
        'OBJECT (from organizationAtom), not its .ref.',
    )
  }
  return id
}

/**
 * Ask the server to reconcile this organization's subscription with what it can verify.
 *
 * Serves the free-plan choice, the Android paywall mount effect, and "restore purchases" — the
 * same request in all three cases, because the client's opinion about the plan is not an input.
 *
 * @param organization - The organization object (from `organizationAtom`). Pass the OBJECT.
 * @returns what the server decided. `applied: false` is a normal outcome; read `reason`.
 * @throws if no id can be resolved, or if the request fails.
 */
export const syncOrganizationSubscription = async (
  organization: unknown,
): Promise<SubscriptionSyncResult> => {
  return organizationsApi.syncSubscription(resolveOrganizationId(organization))
}

/**
 * Atlas-staff subscription override (the `/misc` support screen).
 *
 * A genuinely different trust path from the customer paths above and kept separate on purpose:
 * this one DOES carry a plan, and the backend gates it on `users.is_super_admin` rather than the
 * per-org `admin` role — a merchant is the admin of their own organization, so `admin` would not
 * have been a gate at all.
 *
 * ⚠️ Nobody holds `is_super_admin` yet (0 of 944 users, measured 2026-08-05), so this call returns
 * 403 until Atlas staff accounts are provisioned. That is a tightening, not a regression: the
 * screen it replaces wrote the plan straight into Firestore, and `/misc` is registered
 * unconditionally in `AppStack` and reachable by deep link.
 */
export const overrideOrganizationSubscription = async (
  organization: unknown,
  patch: OverrideSubscription,
): Promise<SubscriptionSyncResult> => {
  return organizationsApi.overrideSubscription(resolveOrganizationId(organization), patch)
}
